// src/Pages/ProjectOverview.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';

import archiveIcon from '../assets/icons/archive.svg';
import emptyArt from '../assets/emptyart.jpg';
import dollarIcon from '../assets/icons/dollar.svg';
import ProjectModal from '../Components/ProjectModal';

// ----------------------------------------------------------------
// ProjectCard (memoized; lazy images; supports id→name map)
// ----------------------------------------------------------------
const ProjectCardBase = ({ project, artistNameMap }) => {
  const today = new Date().toISOString().split('T')[0];
  const budgetValue = Number(project.budget);
  const hasBudget   = !Number.isNaN(budgetValue) && budgetValue > 0;
  const isProjectToday =
    project.releaseDate && project.releaseDate.slice(0, 10) === today;

  const options = { day: '2-digit', month: '2-digit', year: '2-digit' };
  const formattedDate = project.releaseDate
    ? new Date(project.releaseDate).toLocaleDateString('en-GB', options).replaceAll('/', '.').toUpperCase()
    : 'No Release Date';

  const cardOpacity = project.isArchived ? 0.5 : 1.0;

  const displayArtist = useMemo(() => {
    const arr = Array.isArray(project.artists) ? project.artists : [];
    return arr
      .map((a) => {
        if (a && typeof a === 'object') return a.name || a.title || '';
        const id = String(a || '');
        return artistNameMap[id] || '';
      })
      .filter(Boolean)
      .join(', ');
  }, [project.artists, artistNameMap]);

  return (
    <Link to={`/project/${project._id}`} style={{ textDecoration: 'none' }}>
      <div
        className="project-card"
        style={{
          width: '260px',
          padding: '20px',
          margin: '8px',
          border: isProjectToday ? '2px solid #32CD32' : '',
          boxShadow: isProjectToday ? '0 0 10px #32CD32' : '',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          backgroundColor: '#1e1e1e',
          borderRadius: '10px',
          color: 'white',
          opacity: cardOpacity,
          transition: 'opacity 0.3s ease',
        }}
      >
        <img
          src={project.coverArt || emptyArt}
          alt="Cover Art"
          loading="lazy"
          style={{
            width: '100%',
            height: 'auto',
            aspectRatio: '1 / 1',
            objectFit: 'cover',
            marginBottom: '12px',
            borderRadius: '10px',
          }}
        />

        <div style={{ fontFamily: 'inherit', flexGrow: 1 }}>
          <h3
            style={{
              margin: 0,
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {hasBudget && (
              <img
                src={dollarIcon}
                alt="Has budget"
                style={{
                  width: '18px',
                  height: '18px',
                  filter:
                    'invert(68%) sepia(51%) saturate(734%) hue-rotate(63deg) brightness(97%) contrast(96%)',
                }}
              />
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {project.title}
            </span>
          </h3>

          <p
            style={{
              margin: 0,
              marginBottom: '6px',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              color: '#ccc',
            }}
          >
            {displayArtist}
          </p>

          <p
            style={{
              margin: 0,
              color: '#aaa',
              fontSize: '0.9rem',
            }}
          >
            {formattedDate}
          </p>
        </div>
      </div>
    </Link>
  );
};
const ProjectCard = React.memo(ProjectCardBase, (a, b) => {
  return (
    a.project._id === b.project._id &&
    a.project.title === b.project.title &&
    a.project.coverArt === b.project.coverArt &&
    a.project.releaseDate === b.project.releaseDate &&
    a.project.isArchived === b.project.isArchived &&
    a.project.budget === b.project.budget &&
    a.artistNameMap === b.artistNameMap
  );
});

// ----------------------------------------------------------------
// Main ProjectOverview Component
// ----------------------------------------------------------------
const ProjectOverview = () => {
  const [projects, setProjects] = useState([]);
  const [allArtists, setAllArtists] = useState([]);

  // paging (snappy)
  const [page, setPage]       = useState(1);
  const [limit]               = useState(24);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const [showModalProject, setShowModalProject] = useState(false);

  // Load filter settings from localStorage on mount
  const [showArchived, setShowArchived] = useState(() => {
    const saved = localStorage.getItem('projectOverview_showArchived');
    return saved === 'true';
  });
  const [showBudgetOnly, setShowBudgetOnly] = useState(() => {
    const saved = localStorage.getItem('projectOverview_showBudgetOnly');
    return saved === 'true';
  });
    const [searchQuery, setSearchQuery] = useState('');

    const [activeCount, setActiveCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [fromDateProject, setFromDateProject] = useState(() => {
    return localStorage.getItem('projectOverview_fromDate') || '';
  });
  const [toDateProject, setToDateProject] = useState(() => {
    return localStorage.getItem('projectOverview_toDate') || '';
  });
  const [isFilterAppliedProject, setIsFilterAppliedProject] = useState(() => {
    const saved = localStorage.getItem('projectOverview_dateFilterApplied');
    return saved === 'true';
  });

  // artist id → name map
  const artistNameMap = useMemo(() => {
    const map = {};
    for (const a of allArtists) {
      if (a && a._id) map[String(a._id)] = a.name || a.title || '';
    }
    return map;
  }, [allArtists]);

// Debounced search (outside useEffect)
const debouncedFetch = useMemo(() => {
  let timeout;
  return (query) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      setProjects([]);
      setPage(1);
      fetchPage(1, true, showArchived, query);
    }, 300);
  };
}, [showArchived]);

// Initial load - use saved archived state from localStorage
useEffect(() => {
  fetchPage(1, true, showArchived, '');
  refreshArtists();
}, []); // eslint-disable-line react-hooks/exhaustive-deps

// Save filter settings to localStorage when they change
useEffect(() => {
  localStorage.setItem('projectOverview_showArchived', String(showArchived));
}, [showArchived]);

useEffect(() => {
  localStorage.setItem('projectOverview_showBudgetOnly', String(showBudgetOnly));
}, [showBudgetOnly]);

useEffect(() => {
  if (fromDateProject) {
    localStorage.setItem('projectOverview_fromDate', fromDateProject);
  } else {
    localStorage.removeItem('projectOverview_fromDate');
  }
}, [fromDateProject]);

useEffect(() => {
  if (toDateProject) {
    localStorage.setItem('projectOverview_toDate', toDateProject);
  } else {
    localStorage.removeItem('projectOverview_toDate');
  }
}, [toDateProject]);

useEffect(() => {
  localStorage.setItem('projectOverview_dateFilterApplied', String(isFilterAppliedProject));
}, [isFilterAppliedProject]);

  const fetchPage = async (targetPage = 1, reset = false, forceShowArchived = null, searchText = '') => {    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('page', String(targetPage));
      
      const useShowArchived = forceShowArchived !== null ? forceShowArchived : showArchived;
      const isSearching = Boolean(searchText && String(searchText).trim());
      // Include archived when searching, regardless of toggle
      params.set('includeArchived', (isSearching || useShowArchived) ? 'true' : 'false');

      params.set(
        'fields',
        'title,coverArt,releaseDate,endDate,artists,projectType,isArchived,budget,costs,labelShare,termYears,syncRights,territory'
      );
            if (searchText) {
        params.set('text', searchText);
      } else {
        params.delete('text');
      }

      const res = await api(`/api/projects/paged?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const { items, total, activeCount: serverActive = 0, archivedCount: serverArchived = 0 } = await res.json();
      setActiveCount(serverActive);
      setArchivedCount(serverArchived);

      setTotal(total);
      if (reset) {
        setProjects(items);
        setPage(1);
      } else {
        setProjects(prev => targetPage === 1 ? items : [...prev, ...items]);
        setPage(targetPage);
      }
    } catch (e) {
      setError(e.message || 'Load error');
    } finally {
      setLoading(false);
    }
  };

  // expose a refresh function for modals (inline-artist create)
  const refreshArtists = async () => {
    try {
      const res = await api('/api/artists');
      if (!res.ok) throw new Error(`refresh artists failed (${res.status})`);
      setAllArtists(await res.json());
    } catch (e) {
      console.error('refresh artists failed', e);
    }
  };

  // Adding a new project
  const handleAddProject = () => {
    setShowModalProject(true);
  };

  // Save handler (called by ProjectModal)
  const handleSaveProject = async (projectData) => {
    if (!projectData.title?.trim()) {
      alert('Project title is required.');
      return;
    }
    try {
      const res = await api('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData),
      });
      if (!res.ok) throw new Error('Failed to create project.');
      await fetchPage(1, true); // refresh first page
      setShowModalProject(false);
    } catch (err) {
      console.error('Failed to save project:', err);
      alert('Failed to save project.');
    }
  };

    // Filter by archived + date
  const handleToggleArchived = () => {
    const newValue = !showArchived;
    setShowArchived(newValue);
    setProjects([]);
    setPage(1);
        fetchPage(1, true, newValue, searchQuery); // ← PASS newValue to avoid async race
  };
  const handleToggleBudget = () => {
    const newValue = !showBudgetOnly;
    setShowBudgetOnly(newValue);
    // localStorage is saved via useEffect
  };
  const handleDateFilterProject = () => {
    setIsFilterAppliedProject(true);
    // localStorage is saved via useEffect
  };
  const resetFilterProject = () => {
    setFromDateProject('');
    setToDateProject('');
    setIsFilterAppliedProject(false);
    // localStorage is cleared via useEffect
  };

  // Sort newest first (undated first, then newest → oldest)
  const sortedProjects = useMemo(() => {
    const arr = [...projects];
    arr.sort((a, b) => {
      if (!a.releaseDate && !b.releaseDate) return 0;
      if (!a.releaseDate) return -1;
      if (!b.releaseDate) return 1;
      return new Date(b.releaseDate) - new Date(a.releaseDate);
    });
    return arr;
  }, [projects]);

  let visibleProjects = sortedProjects.filter((p) => {
    const isSearching = Boolean(searchQuery && String(searchQuery).trim());
    if (!isSearching && !showArchived && p.isArchived) return false;
    return true;
  });
  if (isFilterAppliedProject && (fromDateProject || toDateProject)) {
    visibleProjects = visibleProjects.filter((p) => {
      if (!p.releaseDate) return false;
      const rd = new Date(p.releaseDate);
      const f = fromDateProject ? new Date(fromDateProject) : null;
      const t = toDateProject ? new Date(toDateProject) : null;
      return (!f || rd >= f) && (!t || rd <= t);
    });
  }
  if (showBudgetOnly) {
    visibleProjects = visibleProjects.filter((p) => {
      const budgetValue = Number(p.budget);
      return !Number.isNaN(budgetValue) && budgetValue > 0;
    });
  }

  const hasMore = projects.length < total;

  return (
    <div style={{ padding: '20px' }}>
      <h1 className="header" style={{ marginTop: '10px', marginBottom: '30px', color: 'white' }}>
        Project Overview
      </h1>

      {/* Search + Filters Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          marginBottom: '24px',
          gap: '15px',
          flexWrap: 'wrap',
        }}
      >
        {/* Search Bar + Badges Container */}
        <div style={{ marginLeft: '8px', flexShrink: 0 }}>
          {/* Search Bar */}
          <div style={{ maxWidth: '300px', width: '300px', position: 'relative' }}>
            <svg
              style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '18px',
                height: '18px',
                pointerEvents: 'none',
                zIndex: 1,
              }}
              fill="none"
              stroke="#757575"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Search projects or artists..."
              value={searchQuery}
              onChange={(e) => {
                const val = e.target.value;
                setSearchQuery(val);
                debouncedFetch(val);
              }}
              style={{
                width: '100%',
                padding: '14px 20px 14px 42px',
                fontSize: '1rem',
                backgroundColor: '#2a2a2a',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                outline: 'none',
                transition: 'none',
                boxShadow: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          
          {/* Active / Archived counter below search */}
          <div style={{ 
            display: 'flex', 
            gap: 12, 
            marginTop: 8,
            alignItems: 'center',
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#2a2a2a',
              padding: '6px 12px',
              borderRadius: 8,
              fontSize: 13,
              color: '#ddd',
              fontWeight: 500,
            }}>
              <span style={{ color: '#7eef4f', marginRight: 6, fontWeight: 600 }}>
                {activeCount}
              </span>
              <span style={{ color: '#999' }}>
                {activeCount === 1 ? 'Active' : 'Active'}
              </span>
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#2a2a2a',
              padding: '6px 12px',
              borderRadius: 8,
              fontSize: 13,
              color: '#ddd',
              fontWeight: 500,
            }}>
              <span style={{ color: '#7eef4f', marginRight: 6, fontWeight: 600 }}>
                {archivedCount}
              </span>
              <span style={{ color: '#999' }}>
                {archivedCount === 1 ? 'Archived' : 'Archived'}
              </span>
            </div>
          </div>
        </div>

        {/* Right-side controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginLeft: 'auto', flexWrap: 'wrap' }}>
          <button
            onClick={handleToggleArchived}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              width: '36px',
              height: '36px',
              padding: '0',
              backgroundColor: showArchived ? '#2a2a2a' : 'transparent',
              borderRadius: '50%',
              border: showArchived ? '2px solid #ff9800' : '1px solid #3a3a3a',
              flexShrink: 0,
              transition: 'all 0.2s ease',
              opacity: showArchived ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = showArchived ? '#333' : '#1f1f1f';
              e.currentTarget.style.opacity = '1';
              if (!showArchived) {
                e.currentTarget.style.borderColor = '#4a4a4a';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = showArchived ? '#2a2a2a' : 'transparent';
              e.currentTarget.style.opacity = showArchived ? '1' : '0.5';
              if (!showArchived) {
                e.currentTarget.style.borderColor = '#3a3a3a';
              }
            }}
            title={showArchived ? 'Hide Archived' : 'Show Archived'}
          >
            <img
              src={archiveIcon}
              alt="Archive Toggle"
              style={{
                width: '18px',
                height: '18px',
                filter: 'brightness(0) invert(90%)',
              }}
            />
          </button>

          <button
            onClick={handleToggleBudget}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              width: '36px',
              height: '36px',
              padding: '0',
              backgroundColor: showBudgetOnly ? '#2a2a2a' : 'transparent',
              borderRadius: '50%',
              border: showBudgetOnly ? '2px solid #ff9800' : '1px solid #3a3a3a',
              flexShrink: 0,
              transition: 'all 0.2s ease',
              opacity: showBudgetOnly ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = showBudgetOnly ? '#333' : '#1f1f1f';
              e.currentTarget.style.opacity = '1';
              if (!showBudgetOnly) {
                e.currentTarget.style.borderColor = '#4a4a4a';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = showBudgetOnly ? '#2a2a2a' : 'transparent';
              e.currentTarget.style.opacity = showBudgetOnly ? '1' : '0.5';
              if (!showBudgetOnly) {
                e.currentTarget.style.borderColor = '#3a3a3a';
              }
            }}
            title={showBudgetOnly ? 'Show All Projects' : 'Show Only Projects with Budget'}
          >
            <img
              src={dollarIcon}
              alt="Budget Filter"
              style={{
                width: '18px',
                height: '18px',
                filter: 'invert(68%) sepia(51%) saturate(734%) hue-rotate(63deg) brightness(97%) contrast(96%)',
              }}
            />
          </button>

          {isFilterAppliedProject && (
            <div
              style={{
                marginLeft: '20px',
                marginRight: '20px',
                padding: '5px 15px',
                backgroundColor: '#ff9800',
                color: '#fff',
                borderRadius: '20px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span style={{ marginRight: '10px' }}>Filter Active</span>
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  animation: 'blink 1s infinite',
                }}
              />
            </div>
          )}

          {/* Modern From/To date group */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '8px 12px',
              backgroundColor: '#1f1f1f',
              border: 'none',
              borderRadius: '12px',
              flexWrap: 'nowrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#bbb', fontSize: '0.9rem' }}>From</span>
              <input
                type="date"
                id="fromDateProject"
                className="styled-date-input"
                onChange={(e) => {
                  setFromDateProject(e.target.value);
                }}
                value={fromDateProject}
                style={{
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#bbb', fontSize: '0.9rem' }}>To</span>
              <input
                type="date"
                id="toDateProject"
                className="styled-date-input"
                onChange={(e) => {
                  setToDateProject(e.target.value);
                }}
                value={toDateProject}
                style={{
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          </div>

          <button
            onClick={handleDateFilterProject}
            style={{
              padding: '8px 14px',
              backgroundColor: '#2a2a2a',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontFamily: 'inherit',
            }}
          >
            Apply
          </button>
          {isFilterAppliedProject && (
            <button
              onClick={resetFilterProject}
              style={{
                padding: '8px 14px',
                backgroundColor: 'transparent',
                color: '#ddd',
                border: 'none',
                borderRadius: '10px',
                fontFamily: 'inherit',
              }}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Projects Grid */}
      {error && <div style={{ color:'salmon', marginBottom:12 }}>{error}</div>}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
        }}
      >
        {/* Add project card */}
        <div
          onClick={handleAddProject}
          style={{
            width: '260px',
            height: '260px',
            padding: '20px',
            margin: '8px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: '#1e1e1e',
            borderRadius: '10px',
            cursor: 'pointer',
            color: '#555',
          }}
        >
          <div style={{ fontSize: '3rem', textAlign: 'center' }}>+</div>
        </div>

        {/* Render visible project cards */}
        {visibleProjects.map((proj) => (
          <ProjectCard key={proj._id} project={proj} artistNameMap={artistNameMap} />
        ))}
      </div>

      {/* Load more */}
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        {projects.length < total && (
          <button
            onClick={() => fetchPage(page + 1)}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: '#333',
              border: 'none',
              color: '#fff',
              borderRadius: 6,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>

            {/* Modal */}
      <ProjectModal
        isOpen={showModalProject}
        onClose={() => setShowModalProject(false)}
        onSave={handleSaveProject}
        allArtists={allArtists}
        onArtistsRefresh={refreshArtists}  // 👈 NEW: so inline-create can refresh the list
      />
    </div>
  );
};

export default ProjectOverview;